import React, { useState, useEffect } from 'react';
import { BuilderComponent, Builder, BuilderContent } from '@builder.io/react';
// @ts-expect-error
import { getBuilderData } from '../api/builderContent';
import { graphql, useStaticQuery } from 'gatsby';
import { setPageTitle } from '../utils/fullstory-page-tracking';
const { isPageActive } = require('../api/gatsbyNode.utils');

const isBuilderPreviewingOrEditing = Builder.isPreviewing || Builder.isEditing;

const MODEL_CONFIG = {
    page: {
        urlField: 'url',
        queryField: 'page',
    },
    'landing-page': {
        urlField: 'pageUrl',
        queryField: 'landingPage',
    },
};

interface BuilderPageData {
    url?: string;
    pageUrl?: string;
}

interface BuilderPage {
    data: BuilderPageData;
    content: BuilderContent;
    published: string;
    startDate?: string;
    endDate?: string;
}

interface AllBuilderModels {
    page?: BuilderPage[];
    landingPage?: BuilderPage[];
    [key: string]: BuilderPage[] | undefined;
}

interface BuilderModelsData {
    allBuilderModels?: AllBuilderModels;
}

const findSSGContent = (data: BuilderModelsData, url: string, model: keyof typeof MODEL_CONFIG): any => {
    const config = MODEL_CONFIG[model];
    const queryField = config.queryField || model;
    const urlField = config.urlField;

    const ssgContent = data?.allBuilderModels?.[queryField]?.find((page: BuilderPage) => {
        return (
            page?.data &&
            page.data[urlField as keyof BuilderPageData] === url &&
            page.published === 'published' &&
            isPageActive(page)
        );
    });

    return ssgContent?.content;
};

const getDataFromBuilder = () => {
    try {
        const data = useStaticQuery(graphql`
            query {
                allBuilderModels {
                    page(options: { cachebust: true, limit: 1000000 }) {
                        data {
                            url
                        }
                        content
                        published
                        startDate
                        endDate
                    }
                    landingPage(options: { cachebust: true, limit: 1000000 }) {
                        data {
                            pageUrl
                        }
                        content
                        published
                        startDate
                        endDate
                    }
                }
            }
        `);
        return data;
    } catch (error) {
        console.error('Error fetching data from Builder:', error);
        return null;
    }
};

export function useBuilderPageOverride(url: string, model: keyof typeof MODEL_CONFIG) {
    const data = getDataFromBuilder();
    const ssgContent = findSSGContent(data, url, model);
    const [content, setContent] = useState(ssgContent);

    useEffect(() => {
        const loadContent = async () => {
            if (isBuilderPreviewingOrEditing) {
                const dynamicContent = await getBuilderData(model, { url });
                setContent(dynamicContent?.data ? dynamicContent : null);
            }
        };

        loadContent();
    }, [url, model, data]);

    return { content };
}

// Enhanced HOC that works with both page and landing-page models
export function withBuilderPageOverride<P>(StaticPageComponent: React.ComponentType<P>, url: string, model = 'page') {
    return function EnhancedPageComponent(props: React.PropsWithChildren<P>) {
        try {
            const isLandingPagePlayground =
                typeof window !== 'undefined' && window.location.href.includes('landing-page');
            if (isLandingPagePlayground) {
                model = 'landing-page';
            }
        } catch (error) {
            // Optionally log the error
            console.error('Error checking window.location:', error);
        }

        const { content } = useBuilderPageOverride(url, model as keyof typeof MODEL_CONFIG);

        useEffect(() => {
            if (content?.data?.title) {
                setPageTitle(content.data.title);
            }
        }, [content]);

        if (content) {
            return (
                <main>
                    <div className="hero py-0">
                        <BuilderComponent model={model} content={content} />
                    </div>
                </main>
            );
        }

        return <StaticPageComponent {...props} />;
    };
}
